import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Messaging.css';

interface Message {
  id: string;
  user: string;
  comment: string;
  timestamp: string;
}

interface Chat {
  id: string;
  name: string;
  email: string;
  message: string;
  replies: Message[];
  timestamp: string;
  error?: string;
}


const Messaging: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [chatId] = useState<string | null>(location.state?.chatId || null);
  const [name] = useState(location.state?.name || user?.name || 'Guest');
  
  const [chat, setChat] = useState<Chat | null>(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchChat = useCallback(() => {
    if (!chatId) return;
    const headers: any = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`${API_URL}/api/contact/${chatId}`, { headers })
      .then(res => res.json())
      .then((data: Chat) => {
        if (data && data.error) {
          console.error('Chat error:', data.error);
          navigate('/contact');
        } else if (data && !data.id) {
          console.error('Chat data invalid:', data);
        } else {
          setChat(data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching chat:', err);
        setLoading(false);
      });
  }, [chatId, token, navigate]);

  useEffect(() => {
    if (!chatId) {
      navigate('/contact');
      return;
    }
    fetchChat();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchChat();
      }
    }, 10000); // Check every 10 seconds when tab is active
    return () => clearInterval(interval);
  }, [chatId, fetchChat, navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chat]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    fetch(`${API_URL}/api/contact/${chatId}/reply`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : 'Anonymous'
      },
      body: JSON.stringify({ 
        comment: replyText,
        guestName: name 
      }),
    })
      .then(() => {
        setReplyText('');
        fetchChat();
      })
      .catch(err => console.error('Error sending message:', err));
  };

  if (loading) return <div className="messaging-page-container"><div className="loading-state">Loading chat...</div></div>;

  return (
    <div className="messaging-page-container">
      <div className="chat-window">
        {/* HEADER */}
        <div className="chat-header">
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}>←</button>
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem' }}>Support Chat</h2>
            <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.9 }}>Chatting as {name}</p>
          </div>
        </div>

        {/* MESSAGES AREA */}
        <div ref={scrollRef} className="chat-messages">
          {/* INITIAL USER MESSAGE */}
          {chat && (
            <div className="message-bubble message-mine">
              <p style={{ margin: 0 }}>{chat.message}</p>
              <small style={{ fontSize: '0.65rem', opacity: 0.8, display: 'block', textAlign: 'right', marginTop: '4px' }}>
                {new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </small>
            </div>
          )}

          {/* REPLIES */}
          {chat?.replies.map(r => {
            const isBot = r.user.includes('Bot');
            const isMe = r.user === name;

            return (
              <div key={r.id} className={`message-bubble ${isMe ? 'message-mine' : (isBot ? 'message-bot' : 'message-other')}`}>
                {!isMe && <strong style={{ display: 'block', fontSize: '0.7rem', marginBottom: '4px', color: isBot ? 'var(--primary-color)' : '#666' }}>{r.user}</strong>}
                <p style={{ margin: 0 }}>{r.comment}</p>
                <small style={{ fontSize: '0.65rem', opacity: 0.7, display: 'block', textAlign: isMe ? 'right' : 'left', marginTop: '4px' }}>
                  {new Date(r.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </small>
              </div>
            );
          })}
        </div>

        {/* INPUT AREA */}
        <form onSubmit={handleSend} className="chat-input-area">
          <input 
            type="text" 
            placeholder="Type a message..." 
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            className="chat-input"
          />
          <button type="submit" style={{ 
            backgroundColor: 'var(--primary-color)', 
            color: '#fff', 
            border: 'none', 
            width: '45px', 
            height: '45px', 
            borderRadius: '50%', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            ➤
          </button>
        </form>
      </div>
    </div>
  );
};

export default Messaging;
